interface FeatureItem {
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
}

export interface MarketingMedia {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  alt: string;
  poster: string;
  /** One well-encoded H.264 master per demo — no second format (story 4-41). */
  mp4: string;
}

const marketingApiBase = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/$/, '');

export function marketingAssetUrl(filename: string): string {
  return `${marketingApiBase}/marketing/assets/${encodeURIComponent(filename)}`;
}

function media(
  id: string,
  eyebrow: string,
  title: string,
  description: string,
  alt: string,
): MarketingMedia {
  const basename = `sitewise-${id}`;
  return {
    id,
    eyebrow,
    title,
    description,
    alt,
    poster: marketingAssetUrl(`${basename}-poster.png`),
    mp4: marketingAssetUrl(`${basename}.mp4`),
  };
}

export const marketingMedia: MarketingMedia[] = [
  media(
    'form-builder',
    'Form Builder',
    'Build the inspection your team actually runs.',
    'Create structured sections and required questions, then move directly into the inspector preview.',
    'DefectSnap Form Builder moving from an editable inspection canvas to an inspector preview',
  ),
  media(
    'template-creation',
    'Flexible starts',
    'Start with intent. Arrive at an editable draft.',
    'Choose a maintained starter, describe the outcome to AI, import an existing sample, duplicate, or begin blank.',
    'DefectSnap showing five controlled paths for creating an inspection template',
  ),
  media(
    'template-versioning',
    'Safe version history',
    'Improve a workflow without disrupting work underway.',
    'Current inspections keep the version they started with while the next update is prepared and released.',
    'DefectSnap template editor opening version history for a published template',
  ),
  media(
    'inspection-to-report',
    'Field to report',
    'Move from field evidence to a finished report.',
    'Keep inspection details, completion, report creation, and the report library in one simple journey.',
    'DefectSnap moving from an inspection detail through completion to the report library',
  ),
  media(
    'access-and-roles',
    'Access & roles',
    'Assign people, roles, and rules in one place.',
    'Give each person the access they need with reusable roles and clear, reviewable rules.',
    'DefectSnap access settings showing members, roles, and the permission matrix',
  ),
  media(
    'governed-agent-plan',
    'AI with approval',
    'The AI prepares the plan. Your team approves it.',
    'A complex request becomes one clear plan that an owner can review before anything changes.',
    'DefectSnap agent presenting a three-step organization plan for explicit owner approval',
  ),
  media(
    'mobile-field-experience',
    'Made for the field',
    'The field experience, at mobile scale.',
    'The inspection workspace adapts to smaller screens while keeping evidence actions and connection status easy to use.',
    'DefectSnap inspection workspace displayed on a mobile viewport',
  ),
];

export const featurePillars: FeatureItem[] = [
  {
    eyebrow: 'Create',
    title: 'Five controlled ways to start',
    description: 'Start with what you already have, then shape it in one easy editor.',
    bullets: [
      'Choose a starter, describe it to AI, import a document, copy an existing template, or begin blank',
      'Move every starting point into the same familiar editor',
      'Review AI-created content before it goes live',
    ],
  },
  {
    eyebrow: 'Build',
    title: 'A structured form-building workspace',
    description: 'Create the questions, choices, and evidence your team needs to do consistent work.',
    bullets: [
      'Organize templates into reusable sections and question types',
      'Make important answers and evidence required',
      'Preview exactly what your team will see before launch',
    ],
  },
  {
    eyebrow: 'Release',
    title: 'Safe publishing and version history',
    description: 'Improve a workflow without changing the rules underneath work already in progress.',
    bullets: [
      'Move through draft, review, publish, archive, and restore',
      'Let current inspections finish on the version they started with',
      'Warn people when a teammate has already made a newer change',
    ],
  },
  {
    eyebrow: 'Control',
    title: 'The right access for every person',
    description: 'Keep owners, managers, inspectors, reviewers, and clients focused on what they are allowed to do.',
    bullets: [
      'Assign ready-made or custom roles to team members',
      'Make access rules clear and easy to review',
      'Show each person only the tools and areas available to them',
    ],
  },
  {
    eyebrow: 'Delegate',
    title: 'AI teammates with clear checkpoints',
    description: 'Let AI do the preparation while people stay in control of important changes.',
    bullets: [
      'Give each AI teammate a focused job and a limited set of actions',
      'Check every requested action before it can run',
      'Turn a complex request into one simple approval plan',
    ],
  },
  {
    eyebrow: 'Operate',
    title: 'One path from inspection to report',
    description: 'Keep the form, field evidence, completion, and final report connected.',
    bullets: [
      'Give every inspection a clear, approved starting point',
      'Keep photo and evidence actions easy to use on smaller screens',
      'Move completed inspections into reports without entering the same information again',
    ],
  },
  {
    eyebrow: 'Extend',
    title: 'Ready for different industries',
    description: 'Keep the same trusted operating model while changing the words, forms, and features for each domain.',
    bullets: [
      'Add new areas without rebuilding the whole application',
      'Keep navigation and access rules consistent as the product grows',
      'Adapt industry language without changing the core workflow',
    ],
  },
];

export const workflowHighlights = [
  'Start: import a PDF (even scanned) or describe the form to the AI agent',
  'Build: the agent assembles pages, sections, tables, response sets, and logic live',
  'Refine: drag-sort questions, wire smart rules, and preview the fill-in experience',
  'Style: design the report once — logo, colors, layout — and save it to your library',
  'Deliver: branded PDF and web reports with scores, flagged items, and media summaries',
];

export const pricingPlans = [
  {
    name: 'Free Trial',
    headline: 'Try the full AI workflow on a single property.',
    price: 'Free trial',
    description: 'One full inspection export with every AI tool included.',
    perks: ['1 inspection included', 'AI form builder & PDF import', 'AI summaries with watermark', 'Standard PDF exports'],
    cta: 'Start for free',
  },
  {
    name: 'Pro',
    headline: 'Scale inspections across your property portfolio.',
    price: '$25 / month',
    description: 'Unlock full DefectSnap automation for fast-moving teams.',
    perks: ['20 inspections per month', 'Unlimited AI template building & imports', 'Branded reports + saved style library', 'Priority support'],
    cta: 'Upgrade to Pro',
  },
  {
    name: 'Enterprise',
    headline: 'Custom workflows for large agencies and franchises.',
    price: 'Talk to us',
    description: 'Tailored onboarding, SSO, and advanced integrations.',
    perks: ['Unlimited reports', 'Dedicated success manager', 'PropertyMe, Console Cloud, API access', 'Custom data residency'],
    cta: 'Book a demo',
  },
];

export const securityHighlights = [
  {
    title: 'Your workspace stays yours',
    items: [
      'Every signed-in person works inside the correct organization',
      'Company data is always looked up within that organization’s workspace',
      'Platform-wide support access is separated from normal customer access',
    ],
  },
  {
    title: 'The right access for each person',
    items: [
      'Roles decide what owners, managers, inspectors, reviewers, and clients can do',
      'Plan limits and available features are checked as well as the person’s role',
      'Blocked access and important changes are recorded for review',
    ],
  },
  {
    title: 'AI only gets the actions it needs',
    items: [
      'Each AI teammate has a focused job and a limited action list',
      'Every action is checked before it can run',
      'Important changes wait for approval, and a plan stops safely if a step fails',
    ],
  },
  {
    title: 'Private photos and reports',
    items: [
      'Inspection media is kept in private storage rather than a public folder',
      'Short-lived private links are created only when a file is needed',
      'Storage credentials never need to be placed in the customer’s browser',
    ],
  },
  {
    title: 'Updates do not rewrite live work',
    items: [
      'Published versions keep a stable history',
      'Work already underway stays on the version it began with',
      'The app decides which review or publishing action is allowed next',
    ],
  },
  {
    title: 'A clear history of important actions',
    items: [
      'AI actions show whether they were proposed, approved, declined, completed, or failed',
      'Multi-step plans show the result of every step',
      'Supported safeguards stop the same accepted action from being run twice by mistake',
    ],
  },
];
